import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { 
  Car, Wrench, Film, Settings, CheckCircle, ChevronRight, Phone
} from "lucide-react";
import { Button } from "../components/ui/button";

// Images fournies par le client
const SERVICE_IMAGE_1 = "https://customer-assets.emergentagent.com/job_my-vitreo-preview/artifacts/98ps50mz_IMG_8930.jpeg"; // Remplacement pare-brise
const SERVICE_IMAGE_2 = "https://customer-assets.emergentagent.com/job_my-vitreo-preview/artifacts/q2nk5zyo_IMG_8929.jpeg"; // Réparation
const SERVICE_IMAGE_TINT = "https://customer-assets.emergentagent.com/job_my-vitreo-preview/artifacts/j1u2ubxo_IMG_9100.png"; // Films teintés
const SERVICE_IMAGE_ADAS = "https://customer-assets.emergentagent.com/job_my-vitreo-preview/artifacts/30ugpheh_IMG_9102.jpeg"; // Calibrage ADAS

const services = [
  {
    id: "pare-brise",
    icon: Car,
    title: "Remplacement de pare-brise",
    description: "Un impact ou une fissure sur votre pare-brise ? Nous intervenons rapidement pour le remplacer à domicile avec un vitrage de qualité équivalente à l'origine.",
    image: SERVICE_IMAGE_1,
    benefits: [
      "Intervention à domicile ou sur lieu de travail",
      "Vitrages certifiés qualité OEM",
      "Prise en charge assurance facilitée",
      "Garantie à vie contre les défauts de fabrication",
      "Délai d'intervention sous 24-48h",
    ],
  },
  {
    id: "reparation",
    icon: Wrench,
    title: "Réparation de vitrage (impact)",
    description: "Pour les petits impacts, une réparation peut suffire et éviter le remplacement complet. Intervention rapide de 30 minutes en moyenne.",
    image: SERVICE_IMAGE_2,
    benefits: [
      "Économique vs remplacement complet",
      "Intervention en 30 minutes",
      "Résultat invisible à 95%",
      "Préserve le vitrage d'origine",
      "Pas de franchise dans la plupart des cas",
    ],
  },
  {
    id: "films-teintes",
    icon: Film,
    title: "Pose de films teintés",
    description: "Améliorez le confort et l'esthétique de votre véhicule avec nos films solaires homologués. Protection contre les UV et la chaleur garantie.",
    image: SERVICE_IMAGE_TINT,
    benefits: [
      "Films homologués route",
      "Réduction chaleur jusqu'à 60%",
      "Protection UV à 99%",
      "Préservation de l'habitacle",
      "Large choix de teintes",
    ],
  },
  {
    id: "calibrage-adas",
    icon: Settings,
    title: "Calibrage ADAS",
    description: "Après tout remplacement de pare-brise sur véhicule équipé, le calibrage des systèmes ADAS (aide à la conduite) est indispensable pour votre sécurité.",
    image: SERVICE_IMAGE_ADAS,
    benefits: [
      "Équipement de calibrage dernière génération",
      "Techniciens certifiés",
      "Compatible toutes marques",
      "Garantie de fonctionnement",
      "Indispensable pour la sécurité",
    ],
  },
];

export default function ServicesPage() {
  return (
    <div className="pt-20 sm:pt-24 lg:pt-28" data-testid="services-page">
      {/* Hero */}
      <section className="section-padding bg-slate-900">
        <div className="max-w-7xl mx-auto text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <span className="inline-block px-4 py-2 bg-sky-500/20 text-sky-400 rounded-full text-sm font-medium mb-6">
              Notre expertise
            </span>
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-6">
              Nos services de vitrage automobile
            </h1>
            <p className="text-xl text-slate-300 max-w-3xl mx-auto">
              MY VITREO vous propose une gamme complète de services pour l'entretien 
              et la réparation du vitrage de votre véhicule.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Services Detail */}
      <section className="section-padding">
        <div className="max-w-7xl mx-auto">
          <div className="space-y-24">
            {services.map((service, index) => (
              <motion.div
                key={service.id}
                id={service.id}
                initial={{ opacity: 0, y: 40 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                className={`grid lg:grid-cols-2 gap-12 items-center ${
                  index % 2 === 1 ? "lg:flex-row-reverse" : ""
                }`}
              >
                <div className={index % 2 === 1 ? "lg:order-2" : ""}>
                  <div className="flex items-center gap-3 mb-4">
                    <div className="w-12 h-12 bg-sky-500/20 rounded-xl flex items-center justify-center">
                      <service.icon className="w-6 h-6 text-sky-400" />
                    </div>
                    <span className="text-sky-400 font-medium uppercase tracking-wider text-sm">
                      Service #{index + 1}
                    </span>
                  </div>
                  
                  <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
                    {service.title}
                  </h2>
                  
                  <p className="text-lg text-slate-300 mb-8 leading-relaxed">
                    {service.description}
                  </p>

                  <h3 className="text-lg font-semibold text-white mb-4">
                    Les avantages :
                  </h3>
                  <ul className="space-y-3 mb-8">
                    {service.benefits.map((benefit) => (
                      <li key={benefit} className="flex items-start gap-3">
                        <CheckCircle className="w-5 h-5 text-green-500 flex-shrink-0 mt-0.5" />
                        <span className="text-slate-300">{benefit}</span>
                      </li>
                    ))}
                  </ul>

                  <div className="flex flex-col sm:flex-row gap-4">
                    <Link to="/devis">
                      <Button className="w-full sm:w-auto bg-sky-500 hover:bg-sky-600 text-white rounded-full px-6" data-testid={`devis-btn-${service.id}`}>
                        Demander un devis
                        <ChevronRight className="ml-2 w-4 h-4" />
                      </Button>
                    </Link>
                    <Link to="/rendez-vous">
                      <Button variant="outline" className="w-full sm:w-auto border-2 border-sky-500 text-sky-400 hover:bg-sky-500/20 rounded-full px-6" data-testid={`rdv-btn-${service.id}`}>
                        Prendre rendez-vous
                      </Button>
                    </Link>
                  </div>
                </div>

                <div className={index % 2 === 1 ? "lg:order-1" : ""}>
                  <div className="glass-card p-4 rounded-3xl">
                    <div className="relative overflow-hidden rounded-2xl">
                      <img
                        src={service.image}
                        alt={service.title}
                        className="w-full h-[400px] object-cover"
                      />
                      {/* Blue glass overlay for design consistency */}
                      <div className="absolute inset-0 bg-gradient-to-br from-sky-500/10 via-transparent to-sky-900/20 pointer-events-none" />
                    </div>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="section-padding bg-sky-500">
        <div className="max-w-4xl mx-auto text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-6">
              Un doute sur le service adapté ?
            </h2>
            <p className="text-sky-100 text-lg mb-8">
              Contactez-nous pour un diagnostic gratuit. Nos experts vous conseilleront 
              sur la meilleure solution pour votre véhicule.
            </p>
            <div className="flex flex-col sm:flex-row justify-center gap-4">
              <a href="tel:0695442788">
                <Button className="w-full sm:w-auto bg-white text-sky-400 hover:bg-sky-500/20 rounded-full px-8 py-6 text-lg font-semibold">
                  <Phone className="mr-2 w-5 h-5" />
                  06 95 44 27 88
                </Button>
              </a>
              <Link to="/contact">
                <Button variant="outline" className="w-full sm:w-auto border-2 border-white text-white hover:bg-white/10 rounded-full px-8 py-6 text-lg font-semibold">
                  Nous contacter
                </Button>
              </Link>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
}
